/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useState } from 'react';

import { Button, Typography2, typography2 } from '@coursera/cds-core';

import { sendMessage } from 'bundles/ai-coach-platform/components/patterns/chat/store/dispatcher';
import type { CoachChatConfig } from 'bundles/unified-common/providers/CoachStateProvider';
import {
  getZeroStatePromptPills,
  getZeroStateWelcomeOptions,
  pickRandom,
} from 'bundles/unified-description-page-common/components/coach/chatZeroStateContent';

import _t from 'i18n!nls/xdp';

const PILLS_TO_DISPLAY = 6;

type Props = {
  contextConfig: CoachChatConfig;
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
    padding: var(--cds-spacing-300);
  `,
  headerText: css`
    background: linear-gradient(279deg, #24dbe7 -5.78%, #6923de 83.48%);
    background-clip: text;
    color: transparent;
    display: inline-block;
  `,
  welcome: css`
    margin: 0;
    color: var(--cds-color-grey-800);
  `,
  pillList: css`
    display: flex;
    flex-wrap: wrap;
    gap: var(--cds-spacing-100);
    list-style: none;
    padding: 0;
    margin: 0;
  `,
  pill: css`
    box-shadow: inset 0 0 0 1px var(--cds-color-neutral-stroke-primary-weak);
    color: var(--cds-color-neutral-primary);

    ${typography2.bodySecondary}
  `,
};

const ChatZeroState = ({ contextConfig }: Props) => {
  const [welcome] = useState(() => pickRandom(getZeroStateWelcomeOptions(), 1)[0]);
  const [pills] = useState(() => pickRandom(getZeroStatePromptPills(), PILLS_TO_DISPLAY));

  const handlePillClick = (message: string) => {
    sendMessage({
      input: { message, ...contextConfig },
      enableContinuousChat: false,
      enableTextStreaming: true,
    });
  };

  return (
    <div css={styles.container} data-testid="chat-zero-state">
      <Typography2 variant="titleXSmall" component="h2">
        <span css={styles.headerText}>{_t('Is this right for me?')}</span>
      </Typography2>
      <Typography2 variant="bodyPrimary" component="p" css={styles.welcome}>
        {welcome}
      </Typography2>
      <ul css={styles.pillList} aria-label={_t('Suggested questions')}>
        {pills.map((pill) => (
          <li key={pill}>
            <Button
              variant="secondary"
              css={styles.pill}
              size="small"
              textAlign="start"
              onClick={() => handlePillClick(pill)}
              data-testid="chat-zero-state-pill"
            >
              {pill}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ChatZeroState;
